import React, { useEffect, useState } from 'react';
// 注意：不再导入 confirmPaymentIntent 和 getElement
import { createElement, init } from '@airwallex/components-sdk';

const Index = () => {
  // ---- State for payment intent data ----
  const [intentId, setIntentId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  // ---- State for element instances ----
  const [cardNumberElement, setCardNumberElement] = useState(null);

  // ---- State for element readiness and completion ----
  const [cardNumberReady, setCardNumberReady] = useState(false);
  const [cvcReady, setCvcReady] = useState(false);
  const [expiryReady, setExpiryReady] = useState(false);
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cvcComplete, setCvcComplete] = useState(false);
  const [expiryComplete, setExpiryComplete] = useState(false);

  // ---- UI states ----
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [intentLoading, setIntentLoading] = useState(true);

  // ---- Input error messages ----
  const [inputErrorMessage, setInputErrorMessage] = useState({
    cardNumber: '卡號必填',
    expiry: '有效期必填',
    cvc: '安全碼必填',
  });

  // ---- 1. Extract order_id from URL and fetch payment intent ----
  useEffect(() => {
    const regex = /\/cardpayment\/([\w]+)/;
    const matches = window.location.href.match(regex);
    if (!matches) {
      setErrorMessage('Invalid payment URL – missing order ID.');
      setIntentLoading(false);
      return;
    }
    const orderId = matches[1];

    const fetchIntent = async () => {
      try {
        const tokenRes = await fetch(
          `https://gbataxi.com/api/payment/get-card-payment-intent-token/${orderId}`,
          { method: 'GET' }
        );
        if (!tokenRes.ok) {
          throw new Error(`Token API error: ${tokenRes.status}`);
        }
        const tokenData = await tokenRes.json();
        const authToken = tokenData.result.Token;

        const intentRes = await fetch(
          'https://gbataxi.com/api/payment/get-card-payment-intent',
          {
            method: 'GET',
            headers: { Authorization: authToken },
          }
        );
        if (!intentRes.ok) {
          throw new Error(`Intent API error: ${intentRes.status}`);
        }
        const intentData = await intentRes.json();
        setIntentId(intentData.result.intent_id);
        setClientSecret(intentData.result.clientSecret);
      } catch (err) {
        setErrorMessage(err.message || 'Failed to load payment details.');
      } finally {
        setIntentLoading(false);
      }
    };

    fetchIntent();
  }, []);

  // ---- 2. Initialise Airwallex and mount split card elements ----
  useEffect(() => {
    if (intentLoading || !clientSecret) return;

    init({
      env: 'prod',
      enabledElements: ['payments'],
      fonts: [
        {
          src: 'https://checkout.airwallex.com/fonts/CircularXXWeb/CircularXXWeb-Regular.woff2',
          family: 'AxLLCircular',
          weight: 400,
        },
      ],
    })
      .then(() => {
        const cardNumEle = createElement('cardNumber');
        const cvcEle = createElement('cvc');
        const expiryEle = createElement('expiry');

        // 保存 cardNumber 实例供后续确认使用
        setCardNumberElement(cardNumEle);

        cardNumEle.mount('cardNumber');
        cvcEle.mount('cvc');
        expiryEle.mount('expiry');
      })
      .catch((err) => {
        setErrorMessage('Failed to initialise payment elements.');
        console.error(err);
      });

    // ---- Event listeners ----
    const onReady = (event) => {
      const { type } = event.detail;
      if (type === 'cardNumber') setCardNumberReady(true);
      if (type === 'cvc') setCvcReady(true);
      if (type === 'expiry') setExpiryReady(true);
    };
    const onChange = (event) => {
      const { type, complete } = event.detail;
      if (type === 'cardNumber') setCardNumberComplete(complete);
      if (type === 'cvc') setCvcComplete(complete);
      if (type === 'expiry') setExpiryComplete(complete);
    };
    const onFocus = (event) => {
      const { type } = event.detail;
      setInputErrorMessage((prev) => ({ ...prev, [type]: '' }));
    };
    const onBlur = (event) => {
      const { type, error } = event.detail;
      setInputErrorMessage((prev) => ({
        ...prev,
        [type]: error?.message ?? (error ? JSON.stringify(error) : ''),
      }));
    };

    const elements = ['cardNumber', 'expiry', 'cvc'];
    elements.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener('onReady', onReady);
        el.addEventListener('onChange', onChange);
        el.addEventListener('onFocus', onFocus);
        el.addEventListener('onBlur', onBlur);
      }
    });

    return () => {
      elements.forEach((id) => {
        const el = document.getElementById(id);
        if (el) {
          el.removeEventListener('onReady', onReady);
          el.removeEventListener('onChange', onChange);
          el.removeEventListener('onFocus', onFocus);
          el.removeEventListener('onBlur', onBlur);
        }
      });
    };
  }, [intentLoading, clientSecret]);

  // ---- 3. Handle payment confirmation (方案一：使用元素实例的 confirm 方法) ----
  const handleConfirm = async () => {
    setIsSubmitting(true);
    setErrorMessage('');

    if (!cardNumberElement) {
      setErrorMessage('Card element not found.');
      setIsSubmitting(false);
      return;
    }

    try {
      // 直接调用 cardNumberElement.confirm()
      const response = await cardNumberElement.confirm({
        id: intentId,
        client_secret: clientSecret,
      });
      window.alert(`Confirm success with ${JSON.stringify(response)}`);
    } catch (error) {
      setErrorMessage(error.message ?? JSON.stringify(error));
      console.error('Payment error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- Derived states ----
  const allElementsReady = cardNumberReady && cvcReady && expiryReady;
  const allElementsComplete = cardNumberComplete && cvcComplete && expiryComplete;
  const formReady = !intentLoading && allElementsReady && !!clientSecret;

  // ---- Styles ----
  const inputStyle = {
    border: '1px solid',
    borderRadius: '5px',
    padding: '5px 10px',
    marginTop: '8px',
    height: '28px',
  };

  return (
    <div>
      {intentLoading && <p>Loading payment details...</p>}
      {!intentLoading && errorMessage && !clientSecret && (
        <p style={{ color: 'red' }}>{errorMessage}</p>
      )}

      {formReady ? (
        <>
          {errorMessage && <p id="error" style={{ color: 'red' }}>{errorMessage}</p>}

          <div className="field-container">
            <div className="field-label">Card number</div>
            <div id="cardNumber" style={inputStyle} />
            <p style={{ color: 'red' }}>{inputErrorMessage.cardNumber}</p>
          </div>

          <div className="field-container">
            <div className="field-label">Expiry</div>
            <div id="expiry" style={inputStyle} />
            <p style={{ color: 'red' }}>{inputErrorMessage.expiry}</p>
          </div>

          <div className="field-container">
            <div className="field-label">CVC</div>
            <div id="cvc" style={inputStyle} />
            <p style={{ color: 'red' }}>{inputErrorMessage.cvc}</p>
          </div>

          <button
            onClick={handleConfirm}
            disabled={!allElementsComplete || isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Confirm'}
          </button>
        </>
      ) : (
        !intentLoading && <p>Waiting for payment elements to load...</p>
      )}
    </div>
  );
};

export default Index;